import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { User, Loader2, CheckCircle, XCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const UsernameSetupModal = () => {
  const { user, updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [error, setError] = useState("");

  // Open modal if user needs username setup
  useEffect(() => {
    if (user?.needs_username_setup) {
      setOpen(true);
    }
  }, [user?.needs_username_setup]);

  // Check username availability with debounce
  useEffect(() => {
    if (!username || username.length < 3) {
      setAvailable(null);
      setError("");
      return;
    }

    // Validate format first
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setAvailable(false);
      setError("Only letters, numbers, and underscores allowed");
      return;
    }

    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const response = await axios.post(`${API_URL}/api/auth/check-username`, { username });
        setAvailable(response.data.available);
        setError(response.data.reason || "");
      } catch (err) {
        setAvailable(null);
        setError("Could not check username");
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!available) {
      toast.error("Please choose an available username");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/api/auth/profile`, { username });
      
      // Update user in context
      if (response.data.user) {
        updateUser({
          ...user,
          username: response.data.user.username,
          needs_username_setup: false
        });
      }
      
      toast.success("Username set successfully!");
      setOpen(false);
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to set username";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if user doesn't need setup
  if (!user?.needs_username_setup) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" hideCloseButton>
        <DialogHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-2">
            <User className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle>Choose Your Username</DialogTitle>
          <DialogDescription>
            Pick a unique username for your hunter profile. This will be visible to other players.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="username"
                type="text"
                placeholder="YourHunterName"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="pl-10 pr-10"
                required
                minLength={3}
                maxLength={30}
                autoFocus
                data-testid="username-setup-input"
              />
              {/* Status indicator */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                {!checking && available === true && <CheckCircle className="w-4 h-4 text-green-500" />}
                {!checking && available === false && <XCircle className="w-4 h-4 text-red-500" />}
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}
            {available && (
              <p className="text-sm text-green-500">Username is available!</p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || !available}
            data-testid="username-setup-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Setting username...
              </>
            ) : (
              "Set Username"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UsernameSetupModal;
